import React, { useEffect, useState } from "react";
import { Chip, Paper, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { Box } from "@mui/system";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useSelector } from "react-redux";
import {
  Person,
  PersonCardInteraction,
  PersonViewType,
} from "@microsoft/mgt-react";
import style from "./ViewWorkshop.module.css";
import useCanViewWorkshop from "../../../hooks/useCanViewWorkshop";
import NoMealsIcon from "@mui/icons-material/NoMeals";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import BreakfastDiningIcon from "@mui/icons-material/BreakfastDining";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import getLocation from "../../../helper/WorkshopLocation";

const ViewWorkshop = () => {
  const canView = useCanViewWorkshop();
  const workshop = useSelector((state) => state.workshops.selectedWorkshop);
  const loading = useSelector(
    (state) => state.workshops.status.workshopLoading
  );

  return (
    <>
      {!loading && workshop && canView && (
        <>
          <Box sx={{ margin: "0.5rem" }}>
            <Paper
              elevation={3}
              sx={{
                backgroundColor: "primary.light",
                color: "specialHeadingText.main",
                padding: "1rem 1rem 1rem 1rem",
                boxSizing: "border-box",
              }}
            >
              {workshop && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                      {workshop.title}
                    </Typography>
                    <Typography variant="h6" sx={{ marginTop: "0.2rem" }}>
                      <CalendarMonthIcon fontSize="inherit" />
                      &nbsp;
                      <span>
                        {DateTime.fromISO(
                          workshop.startDatetime
                        ).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
                      </span>
                      &nbsp;
                      <AccessTimeIcon fontSize="inherit" />
                      &nbsp;
                      <span>
                        <b>
                          {DateTime.fromISO(
                            workshop.startDatetime
                          ).toLocaleString(DateTime.TIME_SIMPLE)}{" "}
                          -{" "}
                          {DateTime.fromISO(
                            workshop.endDatetime
                          ).toLocaleString(DateTime.TIME_SIMPLE)}
                        </b>
                      </span>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>

            <Box className={style.body}>
              <Box
                sx={{
                  padding: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  background: "lightgrey",
                  borderRadius: "4px",
                  alignItems: "top",
                }}
              >
                <EventSeatIcon
                  fontSize="large"
                  sx={{ marginRight: "0.5rem" }}
                />
                <Box>
                  <Typography variant="body1">
                    <b>{workshop.capacity}</b> total seats
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body1">
                    <b>{workshop.seats.numberRegistered}</b> people registered
                  </Typography>
                  <Typography variant="body1">
                    <b>{workshop.waitlists?.length}</b> on waitlist
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Box className={style.section}>
                  <Typography variant="h5">Registration:</Typography>
                  <Typography>
                    {DateTime.fromISO(
                      workshop.registrationStartDate
                    ).toLocaleString(DateTime.DATE_MED)}{" "}
                    -{" "}
                    {DateTime.fromISO(
                      workshop.registrationEndDate
                    ).toLocaleString(DateTime.DATE_MED)}
                  </Typography>
                </Box>

                <Box className={style.section}>
                  <Typography variant="h5">Automatically Remind</Typography>
                  {workshop.daysBeforeToRemind > 0 ? (
                    <>
                      <Typography>
                        An automated reminder will be sent to registrants{" "}
                        <strong>{workshop.daysBeforeToRemind}</strong> days
                        before the workshop starts.
                      </Typography>
                      <Typography>
                        The reminder will be sent on{" "}
                        <strong>
                          {DateTime.fromISO(workshop.startDatetime)
                            .minus({
                              days: workshop.daysBeforeToRemind,
                            })
                            .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
                        </strong>
                      </Typography>
                    </>
                  ) : (
                    <Typography>No reminder set</Typography>
                  )}
                </Box>

                <Box className={style.people}>
                  <Box>
                    <Typography variant="h5">Presented by:</Typography>
                    {workshop?.presenters.length === 0 ? (
                      <Typography
                        variant="h6"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <AccountCircleIcon fontSize="large" />
                        {workshop.externalPresenter}
                      </Typography>
                    ) : (
                      workshop.presenters.map((p, index) => (
                        <Person
                          key={index}
                          view={PersonViewType.threelines}
                          personQuery={p.staff.mail}
                          personCardInteraction={PersonCardInteraction.click}
                          //avatarSize="large"
                          showPresence
                          line2Property="jobTitle"
                          line3Property="department"
                        ></Person>
                      ))
                    )}
                  </Box>
                  {workshop?.organizer?.mail && (
                    <Box>
                      <Typography variant="h5">Organized by:</Typography>
                      <Person
                        view={PersonViewType.threelines}
                        personQuery={workshop.organizer.mail}
                        personCardInteraction={PersonCardInteraction.click}
                        //avatarSize="large"
                        showPresence
                        line2Property="jobTitle"
                        line3Property="department"
                      ></Person>
                    </Box>
                  )}
                </Box>

                <Box className={style.section}>
                  <Typography variant="h5">Location:</Typography>
                  {getLocation(workshop)}
                </Box>
                {workshop.locationDescription && (
                  <Box className={style.section}>
                    <Typography variant="h5">
                      Additional location information:
                    </Typography>
                    <Typography>{workshop.locationDescription}</Typography>
                  </Box>
                )}

                {workshop?.focusArea && (
                  <Box className={style.section}>
                    <Typography variant="h5">Focus area:</Typography>
                    <Typography>{workshop.focusArea}</Typography>
                  </Box>
                )}

                {workshop?.targetAudience && (
                  <Box className={style.section}>
                    <Typography variant="h5">Target Audience:</Typography>
                    <Typography>{workshop.targetAudience}</Typography>
                  </Box>
                )}

                <Box className={style.section}>
                  <Typography variant="h5">Meals provided</Typography>
                  {!workshop.breakfast &&
                  !workshop.lunch &&
                  !workshop.snack &&
                  !workshop.dinner ? (
                    <Chip icon={<NoMealsIcon />} label="None" size="small" />
                  ) : (
                    <>
                      {workshop.breakfast && (
                        <Chip
                          icon={<BreakfastDiningIcon />}
                          label="Breakfast"
                          size="small"
                          className={style.meal}
                        />
                      )}
                      {workshop.snack && (
                        <Chip
                          icon={<LocalCafeIcon />}
                          label="Snack"
                          size="small"
                          className={style.meal}
                        />
                      )}
                      {workshop.lunch && (
                        <Chip
                          icon={<LunchDiningIcon />}
                          label="Lunch"
                          size="small"
                          className={style.meal}
                        />
                      )}
                      {workshop.dinner && (
                        <Chip
                          icon={<RestaurantIcon />}
                          label="Dinner"
                          size="small"
                          className={style.meal}
                        />
                      )}
                    </>
                  )}
                </Box>
                {workshop?.tags && (
                  <Box className={style.section}>
                    <Typography variant="h5">Tags:</Typography>
                    <Typography>{workshop.tags}</Typography>
                  </Box>
                )}

                <Box className={style.section}>
                  <Typography variant="h5">Description:</Typography>
                  <Box className={style.description}>
                    <Typography
                      variant="body1"
                      dangerouslySetInnerHTML={{ __html: workshop.description }}
                    ></Typography>
                  </Box>
                </Box>
                <Box className={style.footer}>
                  Workshop ID: {workshop.workshopId}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
      {!canView && (
        <Paper sx={{ margin: "1rem 0.5rem" }}>
          <Box className={style.body}>
            <Typography variant="h3">
              Sorry you do not have access to this workshop
            </Typography>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ViewWorkshop;
